import { appConstants, getCurrentLanguage } from "@abp";
import { message } from "@messages";
import axios, { AxiosError, AxiosResponse } from "axios";

declare const abp: any;

axios.defaults.baseURL = appConstants.remoteServiceBaseUrl;

let isLoggingOut = false;

function forceLogout() {
  if (isLoggingOut) return;
  isLoggingOut = true;

  document.cookie =
    "RasporediMe-AuthToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Secure; SameSite=Strict";
  document.cookie =
    "enc_auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Secure; SameSite=Strict";
  localStorage.clear();
  sessionStorage.clear();
  abp.auth.clearToken();

  // Full reload is intentional here — this runs outside React context
  // (axios interceptor) when the session is truly dead.
  setTimeout(() => {
    window.location.href = "/login";
  }, 0);
}

function decodeJwtExpiry(token: string): Date | null {
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" ? new Date(payload.exp * 1000) : null;
  } catch {
    return null;
  }
}

let signalRReconnectTimer: ReturnType<typeof setTimeout> | null = null;

function reconnectSignalR(encryptedAccessToken: string) {
  if (!abp.signalr) return;

  // Update the QS immediately so any in-flight reconnect uses the latest token
  abp.signalr.qs =
    appConstants.authorization.encrptedAuthTokenName +
    "=" +
    encodeURIComponent(encryptedAccessToken);

  // Debounce the actual stop/connect — concurrent slides on the same boundary
  // would otherwise cause a stop/connect storm and could leave the hub in a
  // half-open state.
  if (signalRReconnectTimer) clearTimeout(signalRReconnectTimer);
  signalRReconnectTimer = setTimeout(() => {
    signalRReconnectTimer = null;
    const connection = abp.signalr?.hubs?.common;
    if (connection) {
      connection.stop().then(() => abp.signalr.connect());
    }
  }, 100);
}

function applySlidingToken(response: AxiosResponse) {
  const newToken = response.headers["x-new-token"];
  const newEncryptedToken = response.headers["x-new-token-encrypted"];
  if (!newToken || !newEncryptedToken) return;

  const expiry = decodeJwtExpiry(newToken);
  if (!expiry) return;

  abp.auth.setToken(newToken, expiry);
  abp.utils.setCookieValue(
    appConstants.authorization.encrptedAuthTokenName,
    newEncryptedToken,
    expiry,
    abp.appPath
  );
  localStorage.setItem("tokenExpiryDate", expiry.toISOString());

  reconnectSignalR(newEncryptedToken);
}

axios.interceptors.request.use(
  (config) => {
    if (abp.auth.getToken()) {
      config.headers.Authorization = `Bearer ${abp.auth.getToken()}`;
    }

    config.headers.set("x-api-key", appConstants.remoteServiceApiKey);
    config.headers.set("Accept-Language", getCurrentLanguage());

    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => {
    applySlidingToken(response);
    return response;
  },
  (error: AxiosError) => {
    if (error.response) applySlidingToken(error.response);

    const { data, status } = (error?.response as AxiosResponse) || {};
    const errorCode = data?.error?.code;

    if (status === 401) {
      forceLogout();
      return Promise.reject(error);
    }

    if (!error.response) return Promise.reject(error);

    switch (errorCode) {
      case 0:
        message.error(data.error.message);
        break;
      case 400:
        message.error(data.error.message);
        break;
      case 403:
        message.error(data.error.message);
        break;
      case 404:
        window.location.href = "/404";
        break;
      case 500:
        message.error(data.error.message);
        break;
    }
    return Promise.reject(error);
  }
);

const responseBody = <T>(response: AxiosResponse<T>) => response.data;

const requests = {
  get: <T>(url: string, body?: {}) =>
    axios.get<T>(url, body).then(responseBody),
  post: <T>(url: string, body: {}) =>
    axios.post<T>(url, body).then(responseBody),
  put: <T>(url: string, body: {}) => axios.put<T>(url, body).then(responseBody),
  delete: <T>(url: string, body?: {}) =>
    axios.delete<T>(url, body).then(responseBody),
};

export default requests;
